import React, { useState, forwardRef } from "react";
import { useThemeApp } from "components/Theme/theme";
import { MaterialIconName, ThemedIcon } from "../../icons";
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { ThemedInputContainer, ThemedInputContainerProps } from "../ThemedInputContainer";
import { Chip, Popper, TextField, Autocomplete, AutocompleteProps as MuiAutocompleteProps, IconButton, InputAdornment, Box } from "@mui/material";


export interface AutocompleteOption {
    label: string;
    value: string;
    icon?: MaterialIconName;
}

export type ReturnMode = 'object' | 'value';

export type ThemedAutocompleteProps = Omit<
    MuiAutocompleteProps<AutocompleteOption, boolean, boolean, boolean>,
    'onChange' | 'renderInput' | 'value'
> &
    Omit<ThemedInputContainerProps, 'children'> & {
        open?: boolean;
        loading?: boolean;
        multiple?: boolean;
        showIcons?: boolean;
        placeholder?: string;
        returnMode?: ReturnMode;
        onOpenModal?: () => void;
        options: AutocompleteOption[];
        onChange?: (value: any) => void;
        value?: AutocompleteOption | AutocompleteOption[] | string | string[] | null;
    };

const CustomPopper = (props: any) => {
    const { colors } = useThemeApp();
    return (
        <Popper
            {...props}
            placement="bottom-start"
            modifiers={[
                {
                    name: 'offset',
                    options: {
                        offset: [0, 20],
                    },
                },
            ]}
            sx={{
                zIndex: 1300,
                '& .MuiAutocomplete-paper': {
                    marginTop: '4px',
                    overflow: 'hidden',
                    borderRadius: '12px',
                    backdropFilter: 'blur(8px)',
                    transition: 'all 0.2s ease',
                    backgroundColor: colors?.card,
                    boxShadow: `0px 8px 24px ${colors?.shadow}`,
                    border: `1px solid ${colors?.border || colors?.primary + '30'}`,
                },
                '& .MuiAutocomplete-listbox': {
                    padding: '8px',
                    '& .MuiAutocomplete-option': {
                        margin: '2px 0',
                        padding: '8px 12px',
                        color: colors?.text,
                        borderRadius: '6px',
                        transition: 'all 0.2s ease',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',

                        '&:hover': {
                            backgroundColor: colors?.primary + '15',
                        },

                        '&[data-focus="true"], &.Mui-focused': {
                            backgroundColor: colors?.primary + '25',
                        },

                        '&[aria-selected="true"]': {
                            backgroundColor: colors?.primary + '30',
                            color: colors?.primary,
                            fontWeight: 500,

                            '&:hover': {
                                backgroundColor: colors?.primary + '40',
                            },
                        },

                        '& .MuiCheckbox-root': {
                            color: colors?.primary,
                        },
                    },
                },
                '& .MuiAutocomplete-loading': {
                    padding: '12px',
                    textAlign: 'center',
                    color: colors?.text,
                },
                '& .MuiAutocomplete-noOptions': {
                    opacity: 0.7,
                    padding: '12px',
                    textAlign: 'center',
                    color: colors?.text,
                },
            }}
        />
    );
};

// Função para converter value baseado no modo de retorno
const convertValue = (
    value: AutocompleteOption | AutocompleteOption[] | string | string[] | null | undefined,
    returnMode: ReturnMode,
    options: AutocompleteOption[],
    multiple: boolean
): any => {
    if (!value) return multiple ? [] : null;

    // Se já é um objeto e queremos objeto, retorna como está
    if (returnMode === 'object') {
        return value;
    }

    // Se queremos value (string)
    if (returnMode === 'value') {
        if (multiple && Array.isArray(value)) {
            // Array de objetos -> array de strings
            if (value.length > 0 && typeof value[0] === 'object') {
                return (value as AutocompleteOption[]).map(v => v.value);
            }
            // Já é array de strings
            return value as string[];
        } else {
            // Valor único
            if (typeof value === 'object' && value !== null) {
                return (value as AutocompleteOption).value;
            }
            // Já é string
            return value as string;
        }
    }

    return value;
};

// Função para encontrar opções baseado no value
const findOptionsByValue = (
    value: string | string[] | null | undefined,
    options: AutocompleteOption[],
    multiple: boolean
): AutocompleteOption | AutocompleteOption[] | null => {
    if (!value) return multiple ? [] : null;

    if (multiple && Array.isArray(value)) {
        return options.filter(opt => value.includes(opt.value));
    } else {
        return options.find(opt => opt.value === value) || null;
    }
};

export const ThemedAutocomplete = forwardRef<HTMLDivElement, ThemedAutocompleteProps>(
    (
        {
            icon,
            label,
            value,
            options,
            onChange,
            placeholder,
            onOpenModal,
            heightContent,
            loading = false,
            disabled = false,
            multiple = false,
            showIcons = false,
            open: externalOpen,
            returnMode = 'object',
            isOptionEqualToValue = (option, value) => {
                if (typeof option === 'string' || typeof value === 'string') {
                    return option === value;
                }
                return option?.value === value?.value;
            },
            ...rest
        },
        ref
    ) => {
        const { colors } = useThemeApp();
        const [inputValue, setInputValue] = useState('');
        const [internalOpen, setInternalOpen] = useState(false);

        const open = externalOpen !== undefined ? externalOpen : internalOpen;

        const autocompleteValue = React.useMemo(() => {
            if (returnMode === 'value') {
                return findOptionsByValue(value as string | string[], options, multiple);
            }
            return value;
        }, [value, options, multiple, returnMode]);

        const handleChange = (_: any, newValue: any) => {
            if (!onChange) return;

            if (returnMode === 'value') {
                if (multiple && Array.isArray(newValue)) {
                    onChange(newValue.map((v: AutocompleteOption) => v.value));
                } else if (newValue) {
                    onChange((newValue as AutocompleteOption).value);
                } else {
                    onChange(multiple ? [] : null);
                }
            } else {
                onChange(newValue);
            }
        };

        const handleOpen = () => {
            if (onOpenModal) {
                onOpenModal();
            } else {
                setInternalOpen(true);
            }
        };

        const handleClose = () => {
            setInternalOpen(false);
        };

        const handleIconClick = (event: React.MouseEvent) => {
            event.stopPropagation();
            if (disabled) return;

            if (open) {
                handleClose();
            } else {
                handleOpen();
            }
        };

        const renderOption = (props: any, option: AutocompleteOption) => (
            <li {...props}>
                {showIcons && option.icon && (
                    <Box sx={{ mr: 1, display: 'flex', alignItems: 'center' }}>
                        <ThemedIcon
                            size={20}
                            color="primary"
                            name={option.icon}
                        />
                    </Box>
                )}
                <span>{option.label}</span>
            </li>
        );

        return (
            <ThemedInputContainer
                icon={icon}
                label={label}
                heightContent={heightContent}
            >
                <Autocomplete
                    ref={ref}
                    open={open}
                    options={options}
                    loading={loading}
                    multiple={multiple}
                    onOpen={handleOpen}
                    disabled={disabled}
                    onClose={handleClose}
                    inputValue={inputValue}
                    onChange={handleChange}
                    value={autocompleteValue}
                    loadingText="Carregando..."
                    renderOption={renderOption}
                    PopperComponent={CustomPopper}
                    noOptionsText="Nenhuma opção encontrada"
                    isOptionEqualToValue={isOptionEqualToValue}
                    onInputChange={(_, newInputValue) => setInputValue(newInputValue)}
                    getOptionLabel={(option) => {
                        if (typeof option === 'string') return option;
                        return option.label || '';
                    }}
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        height: "100% !Important",
                        padding: "0px !Important",
                        width: "100% !Important",
                        "& .MuiOutlinedInput-root": {
                            padding: "0px !Important",
                        },
                        "& .MuiAutocomplete-root .MuiOutlinedInput-root": {
                            padding: "0px !Important",
                        },
                        "& .MuiFormControl-root": {
                            overflow: "visible !Important"
                        }
                    }}
                    renderTags={(value, getTagProps) =>
                        value.map((option, index) => {
                            const { key, ...tagProps } = getTagProps({ index });
                            return (
                                <Chip
                                    key={key}
                                    {...tagProps}
                                    size="small"
                                    label={option.label}
                                    icon={showIcons && option.icon ? (
                                        <ThemedIcon
                                            name={option.icon}
                                            size={16}
                                            color="primary"
                                        />
                                    ) : undefined}
                                    sx={{
                                        fontSize: 10,
                                        color: colors?.text,
                                        backgroundColor: colors?.primary + '20',
                                        borderRadius: '4px',
                                        maxWidth: { xs: "80px !Important", sm: "200px !Important" },
                                        '& .MuiChip-deleteIcon': {
                                            color: colors?.primary,
                                            '&:hover': {
                                                color: colors?.primary + 'CC',
                                            },
                                        },
                                        '& .MuiChip-icon': {
                                            color: colors?.primary,
                                            marginLeft: '4px',
                                        },
                                    }}
                                />
                            );
                        })
                    }
                    renderInput={(params) => {
                        const { InputProps } = params;
                        const { endAdornment, ...otherInputProps } = InputProps;

                        return (
                            <TextField
                                {...params}
                                variant="outlined"
                                placeholder={placeholder}
                                InputProps={{
                                    ...otherInputProps,
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton
                                                edge="end"
                                                size="small"
                                                disabled={disabled}
                                                onClick={handleIconClick}
                                            >
                                                <KeyboardArrowDownIcon sx={{ color: colors.primary }} />
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                                sx={{
                                    overflow: "hidden",
                                    width: "100% !Important",
                                    height: "100% !Important",
                                    padding: "0px !Important",
                                    "& .MuiOutlinedInput-root": {
                                        padding: "0px !Important",
                                        "& fieldset": {
                                            borderWidth: 0,
                                        },
                                    },
                                    "& .MuiAutocomplete-inputRoot": {
                                        height: "100% !important",
                                        padding: "0px !important",
                                        "& .MuiAutocomplete-input": {
                                            padding: "0px !important"
                                        }
                                    },
                                    "& .MuiAutocomplete-tag": {
                                        margin: "2px",
                                    },
                                }}
                            />
                        );
                    }}
                    {...rest}
                />
            </ThemedInputContainer>
        );
    }
);

ThemedAutocomplete.displayName = "ThemedAutocomplete";